import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { FaCalendarAlt, FaEye } from "react-icons/fa";
import dayjs from "dayjs";
import styles from "./Academiclist.module.css";
import { CustomButton } from "../../../../components";
import CustomMRTTable from "../../../../components/CustomMRTTable/CustomMRTTable";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import { useSelector } from "react-redux";

const AcademicList = () => {
  const navigate = useNavigate();
  const user = TokenService.getUser();
  const clientData = user?.client_data[0] || {};
  const school_id = clientData?.school_id || "";

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [apiSearch, setApiSearch] = useState("");
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  // Fetch academic list on component mount and when dependencies change
  useEffect(() => {
    if (school_id) {
      fetchAcademicList();
    }
  }, [school_id, pagination.pageIndex, pagination.pageSize, apiSearch]);

  // API call to fetch academic list
  const fetchAcademicList = async () => {
    setLoading(true);
    try {
      const response = await AxiosInstance.get(`/school/academic/${school_id}`);

      if (response?.data?.success) {
        const academicData = response.data.data || [];

        // Transform API response to match table structure
        const transformedData = academicData.map((item) => ({
          id: item.id,
          academic_year: item.academic_year,
          start_year: item.academic_year
            ? item.academic_year.split("-")[0]?.trim()
            : "",
          end_year: item.academic_year
            ? item.academic_year.split("-")[1]?.trim()
            : "",
          from_date: item.start_date
            ? dayjs(item.start_date).format("DD/MM/YYYY")
            : "",
          to_date: item.end_date
            ? dayjs(item.end_date).format("DD/MM/YYYY")
            : "",
          isActive: item.isActive,
          client_id: item.client_id,
          school_id: item.school_id,
        }));

        setData(transformedData);
        setRowCount(transformedData.length);
      } else {
        setData([]);
        setRowCount(0);
      }
    } catch (error) {
      console.error("Error fetching academic list:", error);
      setData([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Filter data based on search
  const filteredData = useMemo(() => {
    if (!apiSearch) return data;
    const searchLower = apiSearch.toLowerCase();
    return data.filter(
      (item) =>
        item.start_year?.toString().toLowerCase().includes(searchLower) ||
        item.end_year?.toString().toLowerCase().includes(searchLower) ||
        item.from_date?.toLowerCase().includes(searchLower) ||
        item.to_date?.toLowerCase().includes(searchLower) ||
        item.academic_year?.toLowerCase().includes(searchLower),
    );
  }, [data, apiSearch]);

  // Paginate data
  const paginatedData = useMemo(() => {
    const start = pagination.pageIndex * pagination.pageSize;
    const end = start + pagination.pageSize;
    return filteredData.slice(start, end);
  }, [filteredData, pagination.pageIndex, pagination.pageSize]);

  const handleAdd = () => {
    navigate("/user/academicCreate");
  };

  const handleView = (row) => {
    navigate("/user/academicCreate", {
      state: {
        ID: row.id,
        data: row,
      },
    });
  };
  const { language, translations } = useSelector((state) => state.language);

  // Columns matching the image
  const columns = useMemo(
    () => [
      {
        accessorKey: "start_year",
        header: translations.labels.startYear,
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "end_year",
        header: translations.labels.endYear,
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "from_date",
        header: translations.labels.fromDate,
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "to_date",
        header: "To Date",
        header: translations.labels.toDate,
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        id: "actions",
        header: translations.labels.actions,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <button
            className={styles.viewBtn}
            onClick={() => handleView(row.original)}
            title="View"
          >
            <FaEye />
          </button>
        ),
      },
    ],
    [],
  );

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerIcon}>
          <FaCalendarAlt />
        </div>
        <h1 className={styles.headerTitle}>Academic Configuration</h1>
      </div>

      {/* Actions */}
      <div className={styles.actions}>
        <CustomButton
          type="button"
          variant="contained"
          className={styles.addButton}
          fullWidth={false}
          onClick={handleAdd}
        >
          {translations.labels.add}

        </CustomButton>
      </div>

      {/* Table */}
      <CustomMRTTable
        columns={columns}
        data={paginatedData}
        loading={loading}
        pagination={pagination}
        rowCount={filteredData.length}
        onPaginationChange={setPagination}
        searchInput={searchInput}
        setSearchInput={setSearchInput}
        enableSearch={false}
        enableFiltering={false}
        onSearchEnter={(value) => {
          setApiSearch(value);
          setPagination((prev) => ({ ...prev, pageIndex: 0 }));
        }}
      />
    </div>
  );
};

export default AcademicList;
